import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { TradingSettings, Set, Rarity } from '../../types/pokemon';
import { Button, Card } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Package2, Star, Save } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { PageLayout } from '../../components/layout/PageLayout';

const AVAILABLE_RARITIES: Rarity[] = ['♛', '☆☆☆', '☆☆', '☆', '♢♢♢♢', '♢♢♢', '♢♢', '♢'];

export const AdminTradingSettings = () => {
  const { user, loading, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [sets, setSets] = useState<Set[]>([]);
  const [settings, setSettings] = useState<TradingSettings>({
    eligibleSets: [],
    eligibleRarities: [],
  });
  const [pageLoading, setPageLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!loading && (!user || !isAdmin)) {
      navigate('/');
      return;
    }

    if (!loading && user && isAdmin && !pageLoading) {
      setPageLoading(true);
      Promise.all([fetchSets(), fetchSettings()])
        .finally(() => setPageLoading(false));
    }
  }, [loading, user, isAdmin, navigate]);

  const fetchSets = async () => {
    const { data, error } = await supabase.from('sets').select('*');
    if (error) {
      console.error('Error fetching sets:', error);
      return;
    }
    if (data) {
      setSets(data);
    }
  };

  const fetchSettings = async () => {
    try {
      // Récupérer la configuration existante
      const { data: existingConfig, error } = await supabase
        .from('trading_settings')
        .select('*')
        .single();

      if (error) {
        console.error('Error fetching settings:', error);
        // Si aucune configuration n'existe, en créer une par défaut
        const { data: newConfig, error: createError } = await supabase
          .from('trading_settings')
          .insert({
            id: crypto.randomUUID(), // Générer un nouvel ID
            eligible_sets: [],
            eligible_rarities: [],
          })
          .select()
          .single();

        if (createError) {
          console.error('Error creating default settings:', createError);
          setSettings({
            eligibleSets: [],
            eligibleRarities: [],
          });
          return;
        }

        if (newConfig) {
          setSettings({
            eligibleSets: newConfig.eligible_sets || [],
            eligibleRarities: newConfig.eligible_rarities || [],
          });
        }
        return;
      }

      // Si on a des données existantes
      if (existingConfig) {
        console.log('Loaded settings:', existingConfig); // Pour déboguer
        setSettings({
          eligibleSets: Array.isArray(existingConfig.eligible_sets) 
            ? existingConfig.eligible_sets 
            : [],
          eligibleRarities: Array.isArray(existingConfig.eligible_rarities) 
            ? existingConfig.eligible_rarities 
            : [],
        });
      }
    } catch (error) {
      console.error('Error in fetchSettings:', error);
      setSettings({
        eligibleSets: [],
        eligibleRarities: [],
      });
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaveStatus('saving');
      
      // Récupérer la configuration existante
      const { data: existingConfig } = await supabase
        .from('trading_settings')
        .select('id')
        .single();

      console.log('Saving settings:', {
        id: existingConfig?.id,
        eligible_sets: settings.eligibleSets,
        eligible_rarities: settings.eligibleRarities,
      }); // Pour déboguer

      const { error } = await supabase
        .from('trading_settings')
        .upsert({
          id: existingConfig?.id || crypto.randomUUID(),
          eligible_sets: settings.eligibleSets,
          eligible_rarities: settings.eligibleRarities,
        }, {
          onConflict: 'id'
        });
      
      if (error) {
        console.error('Error saving settings:', error);
        setSaveStatus('error');
        return;
      }
      
      // Recharger les paramètres après la sauvegarde
      await fetchSettings();
      
      // Invalider le cache après la mise à jour
      queryClient.invalidateQueries({ queryKey: ['trading_settings'] });
      
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Error in handleSaveSettings:', error);
      setSaveStatus('error');
    }
  };

  if (loading || pageLoading || !user || !isAdmin) {
    return (
      <PageLayout disableAds={true}>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout disableAds={true}>
      <div className="p-6 relative z-0">
        <div className="flex justify-between items-center mb-6 relative z-10">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin')}
              icon={<ArrowLeftOutlined />}
            >
              Retour
            </Button>
            <h1 className="text-2xl font-bold">Paramètres des Échanges</h1>
          </div>
          <Button
            type="primary"
            icon={<Save className="w-4 h-4" />}
            onClick={handleSaveSettings}
            loading={saveStatus === 'saving'}
          >
            Sauvegarder
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 relative z-0">
          <Card 
            title={
              <div className="flex items-center gap-2">
                <Package2 className="w-5 h-5" />
                <span>Sets Éligibles</span>
              </div>
            }
            className="h-full"
          >
            <div className="space-y-3 max-h-[calc(100vh-300px)] overflow-y-auto">
              {sets.map((set) => (
                <label key={set.id} className="flex items-center p-2 hover:bg-gray-100 rounded-md cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.eligibleSets.includes(set.name)}
                    onChange={(e) => {
                      setSettings(prev => ({
                        ...prev,
                        eligibleSets: e.target.checked
                          ? [...prev.eligibleSets, set.name]
                          : prev.eligibleSets.filter(name => name !== set.name)
                      }));
                    }}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <span className="ml-3 text-gray-700">{set.name}</span>
                </label>
              ))}
            </div>
          </Card>

          <Card 
            title={
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5" />
                <span>Raretés Éligibles</span>
              </div>
            }
            className="h-full"
          >
            <div className="grid grid-cols-2 gap-4">
              {AVAILABLE_RARITIES.map((rarity) => (
                <label key={rarity} className="flex items-center p-2 hover:bg-gray-100 rounded-md cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.eligibleRarities.includes(rarity)}
                    onChange={(e) => {
                      setSettings(prev => ({
                        ...prev,
                        eligibleRarities: e.target.checked
                          ? [...prev.eligibleRarities, rarity]
                          : prev.eligibleRarities.filter(r => r !== rarity)
                      }));
                    }}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <span className="ml-3 text-2xl">{rarity}</span>
                </label>
              ))}
            </div>
          </Card>
        </div>

        {saveStatus === 'success' && (
          <div className="mt-4 p-3 bg-green-100 text-green-700 rounded-md">
            Paramètres sauvegardés avec succès
          </div>
        )}
        {saveStatus === 'error' && (
          <div className="mt-4 p-3 bg-red-100 text-red-700 rounded-md">
            Erreur lors de la sauvegarde des paramètres
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default AdminTradingSettings; 